import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Spring } from 'react-spring/renderprops';
import './styles.css';
import Avatar from '../../../../components/Avatar';
import AvatarWithSelection from '../../../../components/AvatarWithSelection';
import ListItemStatus from '../../../../components/ListItemStatus';
import LoadMask from '../../../../components/LoadMask';
import PanelContainer from '../../../../components/PanelContainer';
import {ANIMATION_TIMEOUT_INOUT} from '../../../../constants/animation';
import {CONNECT_STATUS} from '../../../../constants/profile';
import {STUDENT_ROLE_ID} from '../../../../constants/roles';
import {NetworkActions} from '../../../../helpers/actions/network';
import LinkBuilder from '../../../../helpers/link-builder';
import {Role} from '../../../../helpers/user';
import useApi from '../../../../hooks/useApi';
import NetworkItemOverviewCandidate from './components/NetworkItemOverviewCandidate';
import NetworkItemOverviewStudent from './components/NetworkItemOverviewStudent';

const NetworkItem = props => {
  const {
    user,
    item,
    actions = {},
    selected = false,
    hasSelectMarker = false,
    isSelectMarkerDisabled,
  } = props;

  const api = useApi();
  const [isDisableActions, setDisableActions] = useState(false);

  const { onSelect, onDeselect, onStatusChangeHandler } = actions;

  const {
    CandidateID,
    ConnectStatus,
    FullName,
    PictureUrl,
    RoleID,
    Token,
  } = item;

  const handleClickSelectMarker = value => {
    if (!isSelectMarkerDisabled && onDeselect && onDeselect) {
      if (value) {
        onDeselect(CandidateID);
      } else {
        onSelect(CandidateID);
      }
    }
  };

  const checkAccess = () => Role.isCandidate(user, true);

  const handleClickConnect = () => {
    if (!isDisableActions && checkAccess()) {
      const action = new NetworkActions(api);
      setDisableActions(true);
      action
        .connect({ id: CandidateID })
        .then(
          () =>
            onStatusChangeHandler &&
            onStatusChangeHandler(CandidateID, CONNECT_STATUS.REQUIRE_APPROVE)
        )
        .finally(() => setDisableActions(false));
    }
  };

  const handleClickAccept = () => {
    if (!isDisableActions && checkAccess()) {
      const action = new NetworkActions(api);
      setDisableActions(true);
      action
        .accept({ token: Token })
        .then(
          response =>
            onStatusChangeHandler &&
            onStatusChangeHandler(
              CandidateID,
              CONNECT_STATUS.CONNECTED,
              response || {}
            )
        )
        .finally(() => setDisableActions(false));
    }
  };

  const handleClickReject = () => {
    if (!isDisableActions && checkAccess()) {
      const action = new NetworkActions(api);
      setDisableActions(true);
      action
        .reject({ token: Token })
        .then(
          response =>
            onStatusChangeHandler &&
            onStatusChangeHandler(
              CandidateID,
              CONNECT_STATUS.NOT_CONNECTED,
              response || {}
            )
        )
        .finally(() => setDisableActions(false));
    }
  };

  const handleClickMessage = () => {
    if (checkAccess()) {
      const action = new NetworkActions(api);
      action.sendMessage(CandidateID, FullName);
    }
  };

  const overview = useMemo(() => {
    return Number(RoleID) === STUDENT_ROLE_ID ? (
      <NetworkItemOverviewStudent user={user} item={item} />
    ) : (
      <NetworkItemOverviewCandidate user={user} item={item} />
    );
  }, [user, item]);

  return (
    <Spring
      from={{ opacity: 0 }}
      to={{ opacity: 1 }}
      config={{ duration: ANIMATION_TIMEOUT_INOUT }}
    >
      {props => (
        <div style={props}>
          <PanelContainer styles={{ padding: 0 }}>
            <article className="network-item">
              {isDisableActions && <LoadMask relative />}
              {hasSelectMarker ? (
                <div className="network-item__avatar">
                  <AvatarWithSelection
                    size={60}
                    name={FullName}
                    image={PictureUrl}
                    selected={selected}
                    onSelect={handleClickSelectMarker}
                  />
                  <ListItemStatus
                    status={ConnectStatus}
                    onClickConnectHandler={handleClickConnect}
                    onClickMessageHandler={handleClickMessage}
                    onClickAcceptHandler={handleClickAccept}
                    onClickRejectHandler={handleClickReject}
                  />
                </div>
              ) : (
                <div className="network-item__avatar">
                  <Avatar
                    size={60}
                    name={FullName}
                    image={LinkBuilder.getCandidatePictureSizeLink(
                      PictureUrl,
                      '60x60'
                    )}
                  />
                  <ListItemStatus
                    status={ConnectStatus}
                    onClickConnectHandler={handleClickConnect}
                    onClickMessageHandler={handleClickMessage}
                    onClickAcceptHandler={handleClickAccept}
                    onClickRejectHandler={handleClickReject}
                  />
                </div>
              )}
              {overview}
            </article>
          </PanelContainer>
        </div>
      )}
    </Spring>
  );
};

NetworkItem.propTypes = {
  user: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
  actions: PropTypes.shape({
    onSelect: PropTypes.func,
    onDeselect: PropTypes.func,
    onStatusChangeHandler: PropTypes.func,
  }).isRequired,
  hasSelectMarker: PropTypes.bool,
  isSelectMarkerDisabled: PropTypes.bool,
};

export default NetworkItem;
